import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EditIcon from "@mui/icons-material/Edit";
import KeyIcon from "@mui/icons-material/Key";

import { API_URL } from "./config";
import {
  getCampusFromToken,
  getAuthHeaders,
} from "./commonfunctions/useAuthHelpers";

const WalletAdminDevicesPage = () => {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: BORDER_FOCUS,
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const groupCardSx = (expanded) => ({
    borderRadius: 2,
    border: "1px solid rgba(25,118,210,0.14)",
    overflow: "hidden",
    backgroundColor: "#fff",
    boxShadow: expanded ? "0 0 0 1px rgba(240,120,40,0.16) inset" : "none",
  });

  const groupHeaderSx = (expanded) => ({
    px: 1.5,
    py: 1.2,
    backgroundColor: expanded ? "rgba(240,120,40,0.08)" : ROW_ALT,
    cursor: "pointer",
    "&:hover": { backgroundColor: ROW_HOVER },
  });

  const deviceLineSx = (idx) => ({
    px: 1.5,
    py: 1,
    pl: 6,
    backgroundColor: idx % 2 === 0 ? "#FFFFFF" : "rgba(25,118,210,0.03)",
    borderTop: "1px solid rgba(25,118,210,0.08)",
    "&:hover": { backgroundColor: "rgba(25,118,210,0.08)" },
  });

  const iconBtnSx = {
    color: "rgba(25,118,210,0.95)",
    "&:hover": { color: THEME_ORANGE, backgroundColor: "rgba(240,120,40,0.10)" },
  };

  const campusCode = getCampusFromToken();

  const emptyMerchantForm = {
    MerchantId: null,
    CampusCode: campusCode || "",
    SubCampusCode: "",
    MerchantCode: "",
    MerchantName: "",
    MerchantType: "OTHER",
    IsActive: 1,
  };

  const emptyDeviceForm = {
    DeviceId: null,
    CampusCode: campusCode || "",
    SubCampusCode: "",
    DeviceCode: "",
    DeviceName: "",
    DeviceType: "POS",
    MerchantId: "",
    AllowedIp: "",
    IsActive: 1,
  };

  const [msg, setMsg] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);

  const [merchants, setMerchants] = useState([]);
  const [devices, setDevices] = useState([]);
  const [options, setOptions] = useState({ SubCampusCode: [] });
  const [loadingLookups, setLoadingLookups] = useState(false);

  const [merchantForm, setMerchantForm] = useState(emptyMerchantForm);
  const [deviceForm, setDeviceForm] = useState(emptyDeviceForm);

  const [generatedSecret, setGeneratedSecret] = useState(null);

  const [editingMerchantId, setEditingMerchantId] = useState(null);
  const [editingDeviceId, setEditingDeviceId] = useState(null);

  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    setMerchantForm((prev) => ({ ...prev, CampusCode: campusCode || "" }));
    setDeviceForm((prev) => ({ ...prev, CampusCode: campusCode || "" }));
  }, [campusCode]);

  useEffect(() => {
    if (!campusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);

        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          headers: getAuthHeaders(),
          params: { CampusCode: campusCode },
        });

        if (cancelled) return;

        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
        });
      } catch (e) {
        console.error(e);
        setMsg({ type: "error", text: "Failed to load dropdowns" });
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [campusCode]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [m, d] = await Promise.all([
        axios.get(`${API_URL}/api/wallet/admin/merchants`, {
          params: { CampusCode: campusCode },
          headers: getAuthHeaders(),
        }),
        axios.get(`${API_URL}/api/wallet/admin/devices`, {
          params: { CampusCode: campusCode },
          headers: getAuthHeaders(),
        }),
      ]);

      const merchantRows = m.data?.rows || m.data || [];
      const deviceRows = d.data?.rows || d.data || [];

      setMerchants(merchantRows);
      setDevices(deviceRows);

      setExpandedGroups((prev) => {
        if (Object.keys(prev).length) return prev;
        const next = {};
        merchantRows.forEach((x) => {
          next[`merchant_${x.MerchantId}`] = false;
        });
        next.no_merchant = false;
        return next;
      });
    } catch (err) {
      setMsg({
        type: "error",
        text: err?.response?.data?.message || "Failed to load data",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!campusCode) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusCode]);

  const groupedData = useMemo(() => {
    const devicesByMerchant = {};
    devices.forEach((d) => {
      const key = d.MerchantId ? String(d.MerchantId) : "NO_MERCHANT";
      if (!devicesByMerchant[key]) devicesByMerchant[key] = [];
      devicesByMerchant[key].push(d);
    });

    const merchantGroups = merchants.map((m) => ({
      key: `merchant_${m.MerchantId}`,
      MerchantId: m.MerchantId,
      MerchantCode: m.MerchantCode,
      MerchantName: m.MerchantName,
      MerchantType: m.MerchantType,
      SubCampusCode: m.SubCampusCode,
      IsActive: m.IsActive,
      devices: devicesByMerchant[String(m.MerchantId)] || [],
    }));

    const noMerchantGroup = {
      key: "no_merchant",
      MerchantId: null,
      MerchantCode: "NO_MERCHANT",
      MerchantName: "No Merchant / Gates / Other Devices",
      MerchantType: "OTHER",
      SubCampusCode: null,
      IsActive: 1,
      devices: devicesByMerchant.NO_MERCHANT || [],
    };

    return [...merchantGroups, noMerchantGroup];
  }, [merchants, devices]);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const resetMerchantForm = () => {
    setMerchantForm({
      ...emptyMerchantForm,
      CampusCode: campusCode || "",
    });
    setEditingMerchantId(null);
  };

  const resetDeviceForm = () => {
    setDeviceForm({
      ...emptyDeviceForm,
      CampusCode: campusCode || "",
    });
    setEditingDeviceId(null);
  };

  const handleEditMerchant = (m) => {
    setEditingMerchantId(m.MerchantId);
    setMerchantForm({
      MerchantId: m.MerchantId,
      CampusCode: campusCode || "",
      SubCampusCode: m.SubCampusCode || "",
      MerchantCode: m.MerchantCode || "",
      MerchantName: m.MerchantName || "",
      MerchantType: m.MerchantType || "OTHER",
      IsActive: Number(m.IsActive ?? 1),
    });
  };

  const handleEditDevice = (d) => {
    setEditingDeviceId(d.DeviceId);
    setDeviceForm({
      DeviceId: d.DeviceId,
      CampusCode: campusCode || "",
      SubCampusCode: d.SubCampusCode || "",
      DeviceCode: d.DeviceCode || "",
      DeviceName: d.DeviceName || "",
      DeviceType: d.DeviceType || "POS",
      MerchantId: d.MerchantId ? String(d.MerchantId) : "",
      AllowedIp: d.AllowedIp || "",
      IsActive: Number(d.IsActive ?? 1),
    });
  };

  const saveMerchant = async () => {
    try {
      const payload = {
        ...merchantForm,
        CampusCode: campusCode,
        IsActive: Number(merchantForm.IsActive || 0),
      };

      if (editingMerchantId) {
        await axios.put(
          `${API_URL}/api/wallet/admin/merchant/${editingMerchantId}`,
          payload,
          { headers: getAuthHeaders() }
        );
        setMsg({ type: "success", text: "Merchant updated" });
      } else {
        await axios.post(
          `${API_URL}/api/wallet/admin/merchant`,
          payload,
          { headers: getAuthHeaders() }
        );
        setMsg({ type: "success", text: "Merchant created" });
      }

      resetMerchantForm();
      await loadData();
    } catch (err) {
      setMsg({
        type: "error",
        text: err?.response?.data?.message || "Failed to save merchant",
      });
    }
  };

  const saveDevice = async () => {
    try {
      const payload = {
        ...deviceForm,
        CampusCode: campusCode,
        MerchantId: deviceForm.MerchantId ? Number(deviceForm.MerchantId) : null,
        IsActive: Number(deviceForm.IsActive || 0),
      };

      if (editingDeviceId) {
        const res = await axios.put(
          `${API_URL}/api/wallet/admin/device/${editingDeviceId}`,
          payload,
          { headers: getAuthHeaders() }
        );
        setGeneratedSecret(res.data?.secret || res.data?.DeviceSecret || null);
        setMsg({ type: "success", text: "Device updated" });
      } else {
        const res = await axios.post(
          `${API_URL}/api/wallet/admin/device`,
          payload,
          { headers: getAuthHeaders() }
        );
        setGeneratedSecret(res.data?.secret || res.data?.DeviceSecret || null);
        setMsg({ type: "success", text: "Device created. Save the secret now." });
      }

      resetDeviceForm();
      await loadData();
    } catch (err) {
      setMsg({
        type: "error",
        text: err?.response?.data?.message || "Failed to save device",
      });
    }
  };

  const regenerateSecret = async (deviceId) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/wallet/admin/device/${deviceId}/regenerate-secret`,
        {},
        { headers: getAuthHeaders() }
      );

      setGeneratedSecret(res.data?.secret || res.data?.DeviceSecret || null);
      setMsg({ type: "success", text: "Device secret regenerated. Save it now." });
      await loadData();
    } catch (err) {
      setMsg({
        type: "error",
        text: err?.response?.data?.message || "Failed to regenerate secret",
      });
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900 }}>
              Wallet Admin - Merchants & Devices
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Create, edit, group, and manage merchants and devices
            </Typography>
          </Box>
          <Typography sx={{ fontWeight: 800 }}>
            Login Campus: {campusCode || "-"}
          </Typography>
        </Box>

        <Box sx={bodySx}>
          {!!msg.text && (
            <Snackbar
              open
              autoHideDuration={3000}
              onClose={() => setMsg({ type: "", text: "" })}
            >
              <Alert severity={msg.type || "info"} sx={{ borderRadius: 2 }}>
                {msg.text}
              </Alert>
            </Snackbar>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <Paper elevation={0} sx={sectionSx}>
                <Box sx={{ pl: 1 }}>
                  <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 2 }}>
                    {editingMerchantId ? "Edit Merchant" : "Create Merchant"}
                  </Typography>

                  <Stack spacing={2}>
                    <TextField
                      select
                      label="SubCampus Code"
                      value={merchantForm.SubCampusCode}
                      onChange={(e) =>
                        setMerchantForm((prev) => ({
                          ...prev,
                          SubCampusCode: e.target.value,
                        }))
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                      disabled={loadingLookups}
                    >
                      <MenuItem value="">None</MenuItem>
                      {(options.SubCampusCode || []).map((x) => (
                        <MenuItem key={x} value={x}>
                          {x}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      label="Merchant Code"
                      value={merchantForm.MerchantCode}
                      onChange={(e) =>
                        setMerchantForm((prev) => ({
                          ...prev,
                          MerchantCode: e.target.value,
                        }))
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                    />

                    <TextField
                      label="Merchant Name"
                      value={merchantForm.MerchantName}
                      onChange={(e) =>
                        setMerchantForm((prev) => ({
                          ...prev,
                          MerchantName: e.target.value,
                        }))
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                    />

                    <TextField
                      select
                      label="Merchant Type"
                      value={merchantForm.MerchantType}
                      onChange={(e) =>
                        setMerchantForm((prev) => ({
                          ...prev,
                          MerchantType: e.target.value,
                        }))
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                    >
                      {["CANTEEN", "LIBRARY", "STORE", "TRANSPORT", "HOSTEL", "OTHER"].map(
                        (x) => (
                          <MenuItem key={x} value={x}>
                            {x}
                          </MenuItem>
                        )
                      )}
                    </TextField>

                    <TextField
                      select
                      label="Active"
                      value={merchantForm.IsActive}
                      onChange={(e) =>
                        setMerchantForm((prev) => ({
                          ...prev,
                          IsActive: e.target.value,
                        }))
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                    >
                      <MenuItem value={1}>Yes</MenuItem>
                      <MenuItem value={0}>No</MenuItem>
                    </TextField>

                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        sx={primaryBtnSx}
                        onClick={saveMerchant}
                        disabled={!campusCode || loading}
                        fullWidth
                      >
                        {editingMerchantId ? "Update Merchant" : "Create Merchant"}
                      </Button>

                      {editingMerchantId && (
                        <Button
                          variant="outlined"
                          sx={outlinedBtnSx}
                          onClick={resetMerchantForm}
                          fullWidth
                        >
                          Cancel
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Paper elevation={0} sx={sectionSx}>
                <Box sx={{ pl: 1 }}>
                  <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 2 }}>
                    {editingDeviceId ? "Edit Device" : "Create Device"}
                  </Typography>

                  <Stack spacing={2}>
                    <TextField
                      select
                      label="SubCampus Code"
                      value={deviceForm.SubCampusCode}
                      onChange={(e) =>
                        setDeviceForm((prev) => ({
                          ...prev,
                          SubCampusCode: e.target.value,
                        }))
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                      disabled={loadingLookups}
                    >
                      <MenuItem value="">None</MenuItem>
                      {(options.SubCampusCode || []).map((x) => (
                        <MenuItem key={x} value={x}>
                          {x}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      label="Device Code"
                      value={deviceForm.DeviceCode}
                      onChange={(e) =>
                        setDeviceForm((prev) => ({
                          ...prev,
                          DeviceCode: e.target.value,
                        }))
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                    />

                    <TextField
                      label="Device Name"
                      value={deviceForm.DeviceName}
                      onChange={(e) =>
                        setDeviceForm((prev) => ({
                          ...prev,
                          DeviceName: e.target.value,
                        }))
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                    />

                    <TextField
                      select
                      label="Device Type"
                      value={deviceForm.DeviceType}
                      onChange={(e) =>
                        setDeviceForm((prev) => ({
                          ...prev,
                          DeviceType: e.target.value,
                        }))
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                    >
                      {["POS", "GATE", "KIOSK", "ADMIN"].map((x) => (
                        <MenuItem key={x} value={x}>
                          {x}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      label="Merchant"
                      value={deviceForm.MerchantId}
                      onChange={(e) =>
                        setDeviceForm((prev) => ({
                          ...prev,
                          MerchantId: e.target.value,
                        }))
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                    >
                      <MenuItem value="">None</MenuItem>
                      {merchants.map((m) => (
                        <MenuItem key={m.MerchantId} value={m.MerchantId}>
                          {m.MerchantCode} - {m.MerchantName}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      label="Allowed IP"
                      value={deviceForm.AllowedIp}
                      onChange={(e) =>
                        setDeviceForm((prev) => ({
                          ...prev,
                          AllowedIp: e.target.value,
                        }))
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                    />

                    <TextField
                      select
                      label="Active"
                      value={deviceForm.IsActive}
                      onChange={(e) =>
                        setDeviceForm((prev) => ({
                          ...prev,
                          IsActive: e.target.value,
                        }))
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                    >
                      <MenuItem value={1}>Yes</MenuItem>
                      <MenuItem value={0}>No</MenuItem>
                    </TextField>

                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        sx={primaryBtnSx}
                        onClick={saveDevice}
                        disabled={!campusCode || loading}
                        fullWidth
                      >
                        {editingDeviceId ? "Update Device" : "Create Device"}
                      </Button>

                      {editingDeviceId && (
                        <>
                          <Button
                            variant="outlined"
                            sx={outlinedBtnSx}
                            onClick={() => regenerateSecret(editingDeviceId)}
                            fullWidth
                          >
                            Update Secret
                          </Button>
                          <Button
                            variant="outlined"
                            sx={outlinedBtnSx}
                            onClick={resetDeviceForm}
                            fullWidth
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </Stack>

                    {!!generatedSecret && (
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        SAVE THIS SECRET NOW: <b>{generatedSecret}</b>
                      </Alert>
                    )}
                  </Stack>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={0} sx={sectionSx}>
                <Box sx={{ pl: 1 }}>
                  <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 2 }}>
                    Merchants and Devices
                  </Typography>

                  {!groupedData.length ? (
                    <Typography sx={{ opacity: 0.75 }}>No data found.</Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {groupedData.map((group) => {
                        const expanded = !!expandedGroups[group.key];
                        return (
                          <Paper key={group.key} elevation={0} sx={groupCardSx(expanded)}>
                            <Box
                              sx={groupHeaderSx(expanded)}
                              onClick={() => toggleGroup(group.key)}
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                  <IconButton size="small" sx={iconBtnSx}>
                                    {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                  </IconButton>

                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 900 }}>
                                      {group.MerchantCode} - {group.MerchantName}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                      Type: {group.MerchantType} | SubCampus: {group.SubCampusCode || "-"} | Active:{" "}
                                      {Number(group.IsActive) ? "Yes" : "No"} | Devices: {group.devices.length}
                                    </Typography>
                                  </Box>
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                  {group.MerchantId && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      sx={outlinedBtnSx}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditMerchant(group);
                                      }}
                                    >
                                      Edit Merchant
                                    </Button>
                                  )}
                                </Stack>
                              </Stack>
                            </Box>

                            {expanded && (
                              <Box>
                                {!group.devices.length ? (
                                  <Box sx={deviceLineSx(0)}>
                                    <Typography sx={{ opacity: 0.75 }}>
                                      No devices under this merchant.
                                    </Typography>
                                  </Box>
                                ) : (
                                  group.devices.map((d, idx) => (
                                    <Box key={d.DeviceId} sx={deviceLineSx(idx)}>
                                      <Stack
                                        direction={{ xs: "column", md: "row" }}
                                        spacing={1}
                                        alignItems={{ xs: "flex-start", md: "center" }}
                                        justifyContent="space-between"
                                      >
                                        <Box>
                                          <Typography sx={{ fontWeight: 800 }}>
                                            {d.DeviceCode} - {d.DeviceName}
                                          </Typography>
                                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                            Type: {d.DeviceType} | SubCampus: {d.SubCampusCode || "-"} | Allowed IP:{" "}
                                            {d.AllowedIp || "-"} | Active: {Number(d.IsActive) ? "Yes" : "No"}
                                          </Typography>
                                        </Box>

                                        <Stack direction="row" spacing={1}>
                                          <IconButton
                                            size="small"
                                            sx={iconBtnSx}
                                            onClick={() => handleEditDevice(d)}
                                            title="Edit Device"
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            sx={iconBtnSx}
                                            onClick={() => regenerateSecret(d.DeviceId)}
                                            title="Update Secret"
                                          >
                                            <KeyIcon fontSize="small" />
                                          </IconButton>
                                        </Stack>
                                      </Stack>
                                    </Box>
                                  ))
                                )}
                              </Box>
                            )}
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default WalletAdminDevicesPage;